import { Component, ChangeDetectionStrategy, effect, inject, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ChargeService } from '../../services/charge.service';
import {
  ChargeOperationalStatusEnum,
  CollectionMethodEnum,
  PaymentRecordStatusEnum,
  type ChargeDTO,
  type ChargeOperationalStatusEnumValue,
  type CollectionAttemptEventDTO,
  type CollectionMethodEnumValue,
  type CustomerDTO,
  type InvoiceDTO,
  type PaymentAttemptDTO,
  type PaymentFormDTO,
  type PaymentRecordStatusEnumValue,
  type SaleDTO
} from '../../core/models';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import { CollectionAttemptTimelineComponent } from '../collection-attempts/collection-attempt-timeline.component';

type ChargeStatus = ChargeOperationalStatusEnumValue | string;

@Component({
  selector: 'app-charge-read',
  templateUrl: './charge-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .charge-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .charge-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [CommonModule, RouterLink, TagModule, DrawerModule, CollectionAttemptTimelineComponent]
})
export class ChargeReadComponent {
  private chargeService = inject(ChargeService);
  private toastr = inject(ToastrService);

  charge = input<ChargeDTO | null>(null);
  visible = model(false);

  paymentAttempts = signal<PaymentAttemptDTO[]>([]);
  paymentAttemptsLoading = signal(false);
  /** Intento con el historial desplegado; el historial de cada intento se carga una vez. */
  expandedAttemptId = signal<number | null>(null);
  attemptEvents = signal<Record<number, CollectionAttemptEventDTO[]>>({});
  attemptEventsLoadingId = signal<number | null>(null);
  private lastLoadedId: number | null = null;

  private readonly statusOptions: { value: ChargeStatus; label: string }[] = [
    { value: ChargeOperationalStatusEnum.PENDING, label: 'Pendiente' },
    { value: ChargeOperationalStatusEnum.DUE, label: 'Vencido' },
    { value: ChargeOperationalStatusEnum.SCHEDULED, label: 'Programado' },
    { value: ChargeOperationalStatusEnum.PAID, label: 'Pagado' },
    { value: ChargeOperationalStatusEnum.FAILED, label: 'Fallido' },
    { value: ChargeOperationalStatusEnum.EXPIRED, label: 'Expirado' },
    { value: ChargeOperationalStatusEnum.CANCELLED, label: 'Cancelado' }
  ];

  private readonly collectionMethodOptions: { value: CollectionMethodEnumValue; label: string }[] = [
    { value: CollectionMethodEnum.LINK, label: 'Liga de pago' },
    { value: CollectionMethodEnum.MANUAL, label: 'Manual' },
    { value: CollectionMethodEnum.RECURRING_CHARGE, label: 'Cobro recurrente' },
    { value: CollectionMethodEnum.MERCHANT_INITIATED, label: 'Cobro bajo demanda' }
  ];

  constructor() {
    effect(() => {
      const charge = this.charge();
      const open = this.visible();
      if (!open || !charge?.id) {
        this.lastLoadedId = null;
        this.paymentAttempts.set([]);
        return;
      }
      if (this.lastLoadedId === charge.id) return;
      this.lastLoadedId = charge.id;
      this.loadPaymentAttempts(charge.id);
    });
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.visible.set(visible);
    if (!visible) {
      this.paymentAttempts.set([]);
      this.paymentAttemptsLoading.set(false);
      this.resetAttemptEvents();
    }
  }

  getStatusLabel(status: ChargeStatus): string {
    return this.statusOptions.find((o) => o.value === status)?.label ?? (status || '-');
  }

  getStatusSeverity(status: ChargeStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | null {
    if (status === ChargeOperationalStatusEnum.PAID) return 'success';
    if (status === ChargeOperationalStatusEnum.PENDING || status === ChargeOperationalStatusEnum.SCHEDULED) return 'info';
    if (status === ChargeOperationalStatusEnum.DUE) return 'warn';
    if (status === ChargeOperationalStatusEnum.CANCELLED || status === ChargeOperationalStatusEnum.FAILED) return 'danger';
    if (status === ChargeOperationalStatusEnum.EXPIRED) return 'secondary';
    return 'secondary';
  }

  getCollectionMethodLabel(method: CollectionMethodEnumValue | string | null | undefined): string {
    if (!method) return '—';
    return this.collectionMethodOptions.find((o) => o.value === method)?.label ?? String(method);
  }

  getChargeSale(charge: ChargeDTO | null | undefined): SaleDTO | undefined {
    return charge?.sale ?? undefined;
  }

  getChargeInvoice(charge: ChargeDTO | null | undefined): InvoiceDTO | undefined {
    return charge?.sale?.invoice ?? charge?.invoice ?? undefined;
  }

  getChargeCustomer(charge: ChargeDTO | null | undefined): CustomerDTO | undefined {
    return charge?.sale?.customer ?? charge?.invoice?.customer ?? undefined;
  }

  getPaymentFormLabel(form: PaymentFormDTO | undefined): string {
    if (!form) return '—';
    const desc = (form.description ?? '').trim();
    if (desc) return desc;
    const name = (form.name ?? '').trim();
    if (name) return name;
    const code = (form.code ?? '').trim();
    return code || '—';
  }

  getPaymentAttemptStatusLabel(status: PaymentRecordStatusEnumValue | string | undefined): string {
    if (status == null || String(status).trim() === '') return '—';
    const labels: Record<string, string> = {
      [PaymentRecordStatusEnum.PAID]: 'Pagado',
      [PaymentRecordStatusEnum.PENDING]: 'Pendiente',
      [PaymentRecordStatusEnum.FAILED]: 'Fallido',
      [PaymentRecordStatusEnum.CANCELLED]: 'Cancelado',
      [PaymentRecordStatusEnum.EXPIRED]: 'Expirado'
    };
    return labels[status] ?? status;
  }

  getPaymentAttemptStatusSeverity(
    status: PaymentRecordStatusEnumValue | string | undefined
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === PaymentRecordStatusEnum.PAID) return 'success';
    if (status === PaymentRecordStatusEnum.PENDING) return 'info';
    if (status === PaymentRecordStatusEnum.FAILED) return 'danger';
    return 'secondary';
  }

  toggleAttemptEvents(attempt: PaymentAttemptDTO): void {
    const chargeId = this.charge()?.id;
    const attemptId = attempt.id;
    if (!chargeId || attemptId == null) return;
    if (this.expandedAttemptId() === attemptId) {
      this.expandedAttemptId.set(null);
      return;
    }
    this.expandedAttemptId.set(attemptId);
    if (this.attemptEvents()[attemptId]) return;
    this.attemptEventsLoadingId.set(attemptId);
    this.chargeService
      .listAttemptEvents(chargeId, attemptId)
      .pipe(finalize(() => this.attemptEventsLoadingId.set(null)))
      .subscribe({
        next: (r) => {
          this.attemptEvents.update((events) => ({ ...events, [attemptId]: r.ok && r.body ? r.body : [] }));
        },
        error: (err) => {
          this.expandedAttemptId.set(null);
          this.toastr.error(httpErrorApiMessage(err, 'Error al cargar el historial del intento'));
        }
      });
  }

  private resetAttemptEvents(): void {
    this.expandedAttemptId.set(null);
    this.attemptEvents.set({});
    this.attemptEventsLoadingId.set(null);
  }

  private loadPaymentAttempts(chargeId: number): void {
    this.resetAttemptEvents();
    this.paymentAttempts.set([]);
    this.paymentAttemptsLoading.set(true);
    this.chargeService
      .listPaymentAttempts(chargeId)
      .pipe(finalize(() => this.paymentAttemptsLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.paymentAttempts.set(r.ok && r.body ? r.body : []);
        },
        error: (err) => {
          this.paymentAttempts.set([]);
          this.toastr.error(httpErrorApiMessage(err, 'Error al cargar los intentos de pago'));
        }
      });
  }
}
